import { FlowjoCommons } from '@portal/portal-core';
import sortBy from 'lodash-es/sortBy';
import isEmpty from 'lodash-es/isEmpty';
import { CUSTOM_FIELD_STATES } from '../types';

const { Net } = FlowjoCommons;

export * from './error-messages';

const filterObject = <T>(
  obj: Record<string, T> = {},
  predicate: (value: T) => boolean = () => true,
): Record<string, T> =>
  Object.entries(obj).reduce<Record<string, T>>(
    (accumulator, [key, value]) =>
      predicate(value) ? { ...accumulator, [key]: value } : accumulator,
    {},
  );

// Raw HTTP response shape returned by Net.sendJson (boundary — resolves as unknown)
interface HttpResponse {
  code: unknown;
  data: string;
}

export interface WrappedHttpSuccess {
  succeeded: true;
  data: unknown;
}

export interface WrappedHttpFailure {
  failed: true;
  error: unknown;
  errorDetail?: unknown;
}

export type WrappedHttpResult = WrappedHttpSuccess | WrappedHttpFailure;

// Wraps HTTP request in safe `succeeded`/`failed` interface
export const wrapHttpRequest = (promise: Promise<unknown>): Promise<WrappedHttpResult> =>
  promise
    .then((response): WrappedHttpResult => {
      // Boundary: Net.sendJson resolves as unknown; JSON.parse yields any
      const { code, data } = response as HttpResponse;
      return code === Net.reponses.OK
        ? { succeeded: true, data: JSON.parse(data).result }
        : { failed: true, error: code, errorDetail: JSON.parse(data).error };
    })
    .catch((err: unknown): WrappedHttpResult => ({ failed: true, error: err }));

interface AuthConfig {
  getAuthToken: () => string | null | undefined;
}

export const sendAction = (
  config: AuthConfig,
  action: unknown,
  url: string,
  timeout?: number,
): Promise<WrappedHttpResult> => {
  const authToken = config.getAuthToken();
  if (authToken) {
    const headers = { Authorization: `Bearer ${authToken}` };
    const rejectUnauthorized = undefined;
    return wrapHttpRequest(Net.sendJson(url, action, headers, rejectUnauthorized, timeout));
  }
  return wrapHttpRequest(Net.sendJson(url, action));
};

interface CustomFieldKey {
  status?: string;
  [key: string]: unknown;
}

/**
 * For a particular user, returns only custom field keys that should be
 * displayed to the end user.
 * <p>
 * @param customFieldKeys - an object which contains all the keys for
 * a product license.
 * @returns an object only containing keys with the specified status.
 */
export const customFieldKeysActiveOnly = (
  customFieldKeys: Record<string, CustomFieldKey> = {},
): Record<string, CustomFieldKey> =>
  filterObject(customFieldKeys, (keyObjects) => keyObjects.status === 'active');

/**
 * For a particular user, returns only custom field keys that should be
 * displayed in the user records table.
 * <p>
 * @param customFieldKeys - an object which contains all the keys for
 * a product license.
 * @returns an object only containing keys with the specified statuses.
 */
export const customFieldKeysActiveAndInactive = (
  customFieldKeys: Record<string, CustomFieldKey> = {},
): Record<string, CustomFieldKey> =>
  filterObject(
    customFieldKeys,
    (keyObjects) => keyObjects.status === 'active' || keyObjects.status === 'inactive',
  );

interface ProductAccess {
  customFieldKeys?: Record<string, CustomFieldKey>;
  id: string;
  name: string;
  users: Record<string, unknown>;
}

interface CostCenter {
  name: string;
  productAccess: ProductAccess[];
}

type CostCenters = Record<string, CostCenter>;

interface UserCustomFieldFormInfo {
  userId: string;
  costCenterId: string;
  productAccessId: string;
  keys: CustomFieldKey[];
  costCenterName: string;
  productName: string;
}

/**
 * Use this utility to filter to only the productAccess objects if user is in productAccess
 * @param userId - A string of the user's uuid.
 * @param costCenters - An object of costCenter or billingUnit objects from userSync data.
 * @returns When an object is returned, it should have the following properties:
 *                                          userId (string), costCenterId (string), productAccessId (string),
 *                                          keys (array of objects), and hasValues(integer). If hasValues is > 0,
 *                                          at least one value has already been supplied for the user.
 */
export function userCustomFieldFormInfo(
  userId: string | undefined,
  costCenters: CostCenters | undefined,
): UserCustomFieldFormInfo | undefined {
  if (!costCenters || !userId) {
    return undefined;
  }

  for (const costCenterId of Object.keys(costCenters)) {
    const costCenter = costCenters[costCenterId];
    const costCenterName = costCenter.name;

    for (const productAccess of costCenter.productAccess) {
      // check if the license is active for the user
      const { customFieldKeys, id, name } = productAccess;
      // check if any of the custom field keys are active
      const activeKeys = customFieldKeysActiveOnly(customFieldKeys);
      // if any of the keys are active, then check if the user has never
      // successfully submitted a value for this license before
      if (activeKeys && !isEmpty(activeKeys) && !productAccess.users[userId]) {
        // transform the keys into the expected format
        // array of objects with shape expected by components
        const keyArray: CustomFieldKey[] = [];
        Object.values(activeKeys).forEach((value) => {
          keyArray.push(value);
        });
        return {
          userId,
          costCenterId,
          productAccessId: id,
          keys: keyArray,
          costCenterName,
          productName: name,
        };
      }
    }
  }
  return undefined;
}

interface CustomFieldForm {
  status?: string;
  orderIndex?: number;
  [key: string]: unknown;
}

export const getActiveFieldsUserForm = (fields?: CustomFieldForm[]): CustomFieldForm[] =>
  fields?.filter(({ status }) => status === CUSTOM_FIELD_STATES.ACTIVE) || [];

export const sortFieldsUserForm = (fields?: CustomFieldForm[]): CustomFieldForm[] => {
  const ordered = sortBy(fields, (field) => field.orderIndex);
  return [...getActiveFieldsUserForm(ordered)];
};
